// webhookTriggerClient — P-104: client-wrappers för webhook_trigger-
// RPCs. Enterprise-only för create; list/disable är öppet för users
// som redan har webhooks (downgrade-safe).
//
// Security-note: \`create()\` returnerar plaintext signing_secret EN
// gång. Klienten BÖR copy-to-clipboard + visa "spara nu"-UX.

import { callRpc } from '@/lib/supabaseTyped';

export interface WebhookTriggerMetadata {
  id: string;
  workflow_id: string;
  path: string;
  label: string;
  disabled_at: string | null;
  last_triggered_at: string | null;
  trigger_count: number;
  created_at: string;
}

export interface CreateWebhookResult {
  success: boolean;
  id?: string;
  path?: string;
  /** Plaintext signing-secret — visas bara EN gång. */
  signing_secret?: string;
  label?: string;
  error?: string;
}

export async function createWebhookTrigger(
  workflowId: string,
  label: string,
): Promise<CreateWebhookResult> {
  const { data, error } = await callRpc<CreateWebhookResult>(
    'create_webhook_trigger',
    { p_workflow_id: workflowId, p_label: label },
  );
  if (error) return { success: false, error: error.message };
  return data ?? { success: false, error: 'empty-response' };
}

export async function listMyWebhookTriggers(
  workflowId?: string,
): Promise<{ success: boolean; triggers: WebhookTriggerMetadata[]; error?: string }> {
  const { data, error } = await callRpc<WebhookTriggerMetadata[]>(
    'list_my_webhook_triggers',
    { p_workflow_id: workflowId ?? null },
  );
  if (error) return { success: false, triggers: [], error: error.message };
  return { success: true, triggers: data ?? [] };
}

export async function disableWebhookTrigger(
  id: string,
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await callRpc<{ success: boolean; error?: string }>(
    'disable_webhook_trigger',
    { p_id: id },
  );
  if (error) return { success: false, error: error.message };
  return data ?? { success: false, error: 'empty-response' };
}

/** Bygg full webhook-URL för en path. */
export function buildWebhookUrl(
  path: string,
  opts: { supabaseUrl?: string } = {},
): string {
  const base =
    opts.supabaseUrl
    ?? (typeof import.meta !== 'undefined' ? import.meta.env?.VITE_SUPABASE_URL : '')
    ?? '';
  const trimmed = String(base || '').replace(/\/+$/, '');
  return `${trimmed}/functions/v1/workflow-webhook/${path}`;
}
